import { Button, Card, CardContent } from "@devhop/ui";
import { Download, FileText } from "lucide-react";
import type { TemplateItemDTO } from "@/modules/document-portal/domain/contracts";
import { resolveImageSrc } from "@/shared/ui/AppImage";

type TemplateDocumentCardProps = {
  item: TemplateItemDTO;
};

function handleDownload(
  fileUrl: string | undefined,
  fileName: string | null | undefined,
) {
  if (!fileUrl) return;
  const resolved = resolveImageSrc(fileUrl);
  if (!resolved) return;
  const link = document.createElement("a");
  link.href = resolved;
  link.download = fileName?.trim() || "template";
  link.rel = "noopener noreferrer";
  link.click();
}

export function TemplateDocumentCard({ item }: TemplateDocumentCardProps) {
  const hasFile = Boolean(item.fileUrl && resolveImageSrc(item.fileUrl));
  const description = item.description?.trim() ?? "";

  return (
    <Card className="overflow-hidden transition-colors hover:border-black/30">
      <CardContent className="p-4 py-0">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-black/5">
              <FileText className="size-5 text-black" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-base">{item.name}</h4>
              <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                {item.category}
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded bg-muted px-2 py-0.5 font-medium text-muted-foreground text-xs">
            {item.fileType}
          </span>
        </div>
        {item.nameEn && (
          <p className="mb-2 text-muted-foreground text-xs">{item.nameEn}</p>
        )}
        {/* Max 2 lines; min height = 2 lines so short text aligns with longer cards; full text on hover */}
        {description ? (
          <div className="mb-4 min-h-[2.5rem]" title={description}>
            <p className="line-clamp-2 text-muted-foreground text-xs leading-relaxed">
              {description}
            </p>
          </div>
        ) : null}
        {hasFile ? (
          <Button
            type="button"
            variant="outline"
            className="w-full border-black font-medium hover:bg-black hover:text-white"
            onClick={() => handleDownload(item.fileUrl, item.fileName)}
          >
            <Download className="mr-2 size-4" />
            ດາວໂຫລດ
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
